"use client";
import { Button } from "@components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@components/ui/drawer";
import { Label } from "@components/ui/label";
import { RadioGroup, RadioGroupItem } from "@components/ui/radio-group";
import { useMediaQuery } from "@lib/utils/hooks/useMediaQuery";
import { baseURL } from "@repo/helpers";
import { Download, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";

type DownloadType = "base" | "reverse";

interface IDownloadDialog {
  id: string;
  name: string;
}

export function DownloadDialog({ id, name }: IDownloadDialog) {
  const [val, setVal] = useState<DownloadType>("base");
  const [open, setOpen] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const t = useTranslations("GPX");
  if (isDesktop) {
    return (
      <Dialog>
        <DialogTrigger className="inline-flex h-10 items-center justify-center gap-4 whitespace-nowrap rounded-md border border-accent bg-transparent px-4 py-2 text-sm font-medium text-accent ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:bg-inactive disabled:text-inactive-foreground">
          {t("DownloadGPX")}{" "}
          <Download strokeWidth="1" className="h-[18px] w-[18px]" />
        </DialogTrigger>
        <DialogContent closeClassName="top-5" className="max-w-[350px]">
          <DialogHeader>
            <DialogTitle>{t("DownloadTitle")}</DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-black">
            {t("DownloadDescription")}
          </DialogDescription>
          <form>
            <legend className="mb-2 text-base font-bold">
              {t("DownloadFormLabel")}
            </legend>

            <RadioGroup
              defaultValue="base"
              onValueChange={(e: DownloadType) => setVal(e)}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="base" id="base" />
                <Label htmlFor="base">{name}</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="reverse" id="reverse" />
                <Label htmlFor="reverse">
                  {name.split("-").reverse().join("-")}
                </Label>
              </div>
            </RadioGroup>
          </form>
          <DialogFooter className="sm:justify-start">
            <a
              download={
                val === "base" ? name : name.split("-").reverse().join("-")
              }
              href={`${baseURL()}/api/v2/gpx/${val}/${id}`}
              className="inline-flex h-10 w-full items-center justify-center gap-4 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:bg-inactive disabled:text-inactive-foreground"
            >
              {t("Download")}
              <Download strokeWidth="1" className="h-[18px] w-[18px]" />
            </a>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger className="inline-flex h-10 items-center justify-center gap-4 whitespace-nowrap rounded-md border border-accent bg-transparent px-4 py-2 text-sm font-medium text-accent ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:bg-inactive disabled:text-inactive-foreground">
        {t("DownloadGPX")}{" "}
        <Download strokeWidth="1" className="h-[18px] w-[18px]" />
      </DrawerTrigger>
      <DrawerContent className="bg-[#fff]">
        <DrawerHeader className="text-left">
          <DrawerTitle className="flex justify-between ">
            {t("DownloadTitle")}
            <Button
              onClick={() => setOpen(false)}
              size="xs"
              className="rounded-full"
            >
              <X className="h-5 w-5" />
              <span className="sr-only">{t("Clear")}</span>
            </Button>
          </DrawerTitle>
          <DrawerDescription className="my-7">
            {t("DownloadDescription")}
          </DrawerDescription>
        </DrawerHeader>
        <form className="mb-4 px-4">
          <legend className="mb-2 text-base font-bold">
            {t("DownloadFormLabel")}
          </legend>

          <RadioGroup
            defaultValue="base"
            onValueChange={(e: DownloadType) => setVal(e)}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="base" id="base" />
              <Label htmlFor="base">{name}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="reverse" id="reverse" />
              <Label htmlFor="reverse">
                {name.split("-").reverse().join("-")}
              </Label>
            </div>
          </RadioGroup>
        </form>
        <DrawerFooter className="border-[#AAAAAA] pt-4">
          <a
            download={
              val === "base" ? name : name.split("-").reverse().join("-")
            }
            href={`${baseURL()}/api/v2/gpx/${val}/${id}`}
            className="inline-flex h-10 items-center justify-center gap-4 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:bg-inactive disabled:text-inactive-foreground"
          >
            {t("Download")}{" "}
            <Download strokeWidth="1" className="h-[18px] w-[18px]" />
          </a>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
